import { FC, useState } from 'react';
import { VisibilityOutlined } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { FileModel } from 'api';
import OnboardingEmailDocumentPreviewDialog from '../OnboardingEmailDocumentPreviewDialog';
import { useOnboardingEmailModalContext } from '../OnboardingEmailModalContext';

const OnboardingEmailInputsPanel: FC = () => {
  const {
    templates,
    selectedTemplateId,
    setSelectedTemplateId,
    checklist,
    toggleDocument,
    isLoadingDocuments,
  } = useOnboardingEmailModalContext();

  const [previewFile, setPreviewFile] = useState<FileModel | null>(null);

  return (
    <Stack spacing={2}>
      <FormControl size="small" fullWidth>
        <InputLabel id="onboarding-email-template-label">Onboarding request template</InputLabel>
        <Select
          labelId="onboarding-email-template-label"
          id="onboarding-email-template"
          label="Onboarding request template"
          value={selectedTemplateId}
          onChange={event => setSelectedTemplateId(event.target.value)}
          MenuProps={{
            disablePortal: true,
            PaperProps: { sx: { maxHeight: 320 } },
          }}
        >
          {templates.map(template => (
            <MenuItem key={template.id} value={template.id}>
              {template.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Documents and questions to request
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Completed items are grayed out and disabled. Uploaded documents can be previewed with the
          eyeball icon; answers appear under each question.
        </Typography>
        {isLoadingDocuments ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Checking uploaded documents and answers…</Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {checklist.map(item => {
              const uploadedFile = item.uploadedFiles?.[0] ?? null;
              const isQuestion = item.kind === 'question';
              const hasAnswer = Boolean(item.answerDisplay?.trim());
              const childQuestions = item.childQuestions ?? [];
              const isComplete = item.alreadyHave;
              const completeSuffix = isQuestion ? ' (answered)' : ' (uploaded)';
              const slug = item.label.replace(/\s+/g, '-').toLowerCase();
              // Completed documents are previewable: swap the checkbox for the
              // eyeball icon. Completed questions keep a disabled checkbox.
              const showPreviewInsteadOfCheckbox = isComplete && !isQuestion && Boolean(uploadedFile);
              return (
                <Box key={`${item.kind}-${item.name}`}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    {showPreviewInsteadOfCheckbox ? (
                      <Tooltip title="Preview uploaded document">
                        <IconButton
                          size="small"
                          onClick={() => setPreviewFile(uploadedFile)}
                          aria-label={`Preview ${item.label}`}
                          id={`onboarding-doc-preview-${slug}`}
                          data-testid="onboarding-doc-preview-button"
                          sx={{ p: 0.75 }}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Checkbox
                        checked={item.checked}
                        disabled={isComplete}
                        onChange={() => toggleDocument(item.name)}
                        id={`onboarding-doc-${slug}`}
                        size="small"
                        sx={{ pt: 0.25 }}
                      />
                    )}
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Typography
                        variant="body2"
                        color={isComplete ? 'text.disabled' : undefined}>
                        {item.label}
                        {isComplete ? completeSuffix : ''}
                      </Typography>
                      {isQuestion && hasAnswer && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ display: 'block', mt: 0.25 }}
                          data-testid="onboarding-question-answer">
                          Answer: {item.answerDisplay}
                        </Typography>
                      )}
                      {isQuestion &&
                        childQuestions.map(child => (
                          <Typography
                            key={child.label}
                            variant="caption"
                            color={child.answered ? 'text.disabled' : 'text.secondary'}
                            sx={{ display: 'block', mt: 0.25, pl: 1 }}
                            data-testid="onboarding-question-child-answer">
                            {child.label}
                            {child.answerDisplay ? `: ${child.answerDisplay}` : ''}
                          </Typography>
                        ))}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
      <OnboardingEmailDocumentPreviewDialog
        open={Boolean(previewFile)}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </Stack>
  );
};

export default OnboardingEmailInputsPanel;
